import { useCallback, useRef, useState } from 'react';

import { z } from 'zod';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useStartAPetitionPageData, useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import { useImageJob } from '#src/app/pages/startAPetitionV2/shared/hooks';

import { getErrorMessage, schema } from './schema';

const MAX_IMAGE_GENERATION_COUNT = 4;

type JobStatus = 'idle' | 'pending' | 'success' | 'error';
type FormErrors = { ask: string | null; description: string | null };

type UseEditReturn = ModelHookResult<
	{
		ask: string;
		description: string;
		mode: string | undefined | null;
		jobStatus: JobStatus;
		imageSrc: string | undefined;
		imageRegenerationLeft: number;
		imageRegenerationCount: number;
		imageRegenerationDisabled: boolean;
		imageButtonLabel: TranslationKey;
		imageButtonIcon: 'loader' | 'ai';
		errors: FormErrors;
		formRef: React.RefObject<HTMLFormElement | null>;
		aiTextDisclaimer: string | undefined;
		aiBannerDescription: string | undefined;
	},
	{
		back: () => void;
		submit: () => void;
		toPreview: () => void;
		uploadImage: () => void;
		regenerateAiImage: () => void;
		resetFieldError: (field: 'ask' | 'description') => void;
		persistDescription: (editorContent: string) => void;
		persistAsk: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
	}
>;

// oxlint-disable-next-line complexity, max-statements, max-lines-per-function
export function useEdit(): UseEditReturn {
	const { typedTranslate } = useI18n();
	const {
		data: { draft },
		actions,
	} = useStartAPetitionPageState();
	const { data: pageData } = useStartAPetitionPageData();
	const track = useTracking();

	const mode = draft?.metadata?.draftMode;

	// petition form actions
	const formRef = useRef<HTMLFormElement>(null);
	const [formErrors, setFormErrors] = useState<FormErrors>({ ask: null, description: null });

	const submit = useCallback(
		(target: 'personal-story' | 'preview') => {
			const formData = new FormData(formRef.current ?? undefined);
			const formDataObject: Record<string, string> = {};
			formData.forEach((value, key) => {
				// oxlint-disable-next-line @typescript-eslint/no-base-to-string
				formDataObject[key] = value.toString();
			});
			const result = schema.safeParse(formDataObject);

			if (!result.success) {
				const { fieldErrors } = z.flattenError(result.error);
				const askError = getErrorMessage(fieldErrors.ask?.[0], typedTranslate);
				const descriptionError = getErrorMessage(fieldErrors.description?.[0], typedTranslate);
				setFormErrors({ ask: askError, description: descriptionError });
				return;
			}

			const petitionDraft = { ask: result.data.ask, description: result.data.description };
			actions.send({ type: 'submit', draft: { petitionDraft, editingDraft: null }, target });
		},
		[actions, typedTranslate],
	);

	const resetFieldError = useCallback((field: 'ask' | 'description') => {
		setFormErrors((prev) => ({ ...prev, [field]: null }));
	}, []);

	const validateField = useCallback(
		(field: 'ask' | 'description', value: string): string | null => {
			const result = schema.shape[field].safeParse(value);
			if (result.success) return null;
			return getErrorMessage(result.error.issues[0]?.message, typedTranslate);
		},
		[typedTranslate],
	);

	// image actions
	const { mutate: createImageJob, status } = useImageJob({ enabled: true });
	const imageSrc = draft?.petitionDraft?.image?.url;
	const imageGenerationCount = draft?.metadata?.imageGenerationCount ?? 0;
	const imageRegenerationDisabled = imageGenerationCount >= MAX_IMAGE_GENERATION_COUNT;

	const currentAsk = draft?.editingDraft?.ask ?? draft?.petitionDraft?.ask ?? '';
	const currentDescription = draft?.editingDraft?.description ?? draft?.petitionDraft?.description ?? '';

	const uploadImage = useCallback(() => {
		void track('sap_add_own_image_click');

		actions.send({ type: 'submit', target: 'media' });
	}, [actions, track]);

	const regenerateAiImage = useCallback(() => {
		if (status === 'pending' || imageRegenerationDisabled) return;
		if (!currentAsk.trim() && !currentDescription.trim()) return;

		void track('sap_generate_ai_image_click', { regenerations: imageGenerationCount });

		const prompt = `${currentAsk} ${currentDescription}`;

		// For regeneration, we want to use the combined prompt of ask and description and not pass personalStory
		const input = { mainPrompt: prompt };
		createImageJob(input);
	}, [createImageJob, currentAsk, currentDescription, imageGenerationCount, imageRegenerationDisabled, status, track]);

	const persistDescription = useCallback(
		(editorContent: string) => {
			const editingDraft = { description: editorContent };
			actions.send({ type: 'patch', draft: { editingDraft } });
			setFormErrors((prev) => ({ ...prev, description: validateField('description', editorContent) }));
		},
		[actions, validateField],
	);

	const persistAsk = useCallback(
		(event: React.FocusEvent<HTMLTextAreaElement>) => {
			const askValue = event.target.value.trim();
			const editingDraft = { ask: askValue };
			actions.send({ type: 'patch', draft: { editingDraft } });
			setFormErrors((prev) => ({ ...prev, ask: validateField('ask', askValue) }));
		},
		[actions, validateField],
	);
	const handleSubmit = useCallback(() => {
		void track('sap_step_submit', { step: 'edit', step_data: {}, step_data_raw: '{}' });

		submit('preview');
	}, [submit, track]);

	const handleBack = useCallback(() => {
		void track('sap_step_back', { step: 'edit' });

		submit('personal-story');
	}, [submit, track]);

	let imageButtonLabel: TranslationKey;
	let imageButtonIcon: 'loader' | 'ai';

	if (status === 'pending') {
		imageButtonLabel = translationKey('corgi.start_a_petition.edit.image_action_buttons.image_loading_button_label');
		imageButtonIcon = 'loader';
	} else if (imageGenerationCount > 0) {
		imageButtonLabel = translationKey('corgi.start_a_petition.edit.image_action_buttons.make_new_image');
		imageButtonIcon = 'ai';
	} else {
		imageButtonLabel = translationKey('corgi.start_a_petition.edit.image_action_buttons.make_image');
		imageButtonIcon = 'ai';
	}

	return {
		data: {
			ask: currentAsk,
			description: currentDescription,
			formRef,
			mode,
			errors: formErrors,
			jobStatus: status,
			imageSrc,
			imageRegenerationLeft: Math.max(0, MAX_IMAGE_GENERATION_COUNT - imageGenerationCount),
			imageRegenerationCount: imageGenerationCount,
			imageRegenerationDisabled,
			imageButtonLabel,
			imageButtonIcon,
			// EU AI Act Art. 50 disclosure copy, carried per locale by the experiment's FCM. Undefined only
			// in states this variant shouldn't render in (fallback resolutions) — the disclaimer is then not
			// rendered and the banner keeps its control description.
			aiTextDisclaimer: pageData.experiments.sapAiTextLabeling.config.copy?.editorDisclaimer,
			aiBannerDescription: pageData.experiments.sapAiTextLabeling.config.copy?.bannerDescription,
		},
		actions: {
			back: handleBack,
			submit: handleSubmit,
			toPreview: () => submit('preview'),
			resetFieldError,
			uploadImage,
			regenerateAiImage,
			persistDescription,
			persistAsk,
		},
	};
}
